import { DestroyRef, inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { Socket } from 'socket.io-client';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../notifications/notification.service';
import { ReservationsService } from '../reservations/reservations.service';
import { INotification } from '../../interfaces/notification.interface';
import { StorageService } from '../../../core/services/storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class SocketsService {
  #ws: Socket | null = null;
  #notificationsService: NotificationService = inject(NotificationService);
  #reservationService: ReservationsService = inject(ReservationsService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #storageService: StorageService = inject(StorageService);
  #platformId = inject(PLATFORM_ID);
  #ngZone: NgZone = inject(NgZone);

  apiUrl: string = environment.baseUrl;
  userHasVisited = this.#storageService.hasItem('HAS_VISITED');
  private connected = false;
  private socketInitialized = false;

  constructor() {
    // Ne pas initialiser socket.io côté serveur
    if (isPlatformBrowser(this.#platformId)) {
      this.initializeSocket();
    }
  }

  private async initializeSocket(): Promise<void> {
    if (this.socketInitialized) {
      return;
    }

    try {
      // Import dynamique pour éviter le chargement côté serveur
      const { io } = await import('socket.io-client');
      // Le transport long-polling de socket.io ré-arme sans cesse une requête :
      // exécuté dans la zone Angular, il empêche ApplicationRef.isStable() de
      // se stabiliser (NG0506) et bloque l'hydratation pendant 10s à chaque
      // chargement. On le crée hors zone et on rentre dans la zone uniquement
      // pour les événements qui doivent déclencher une mise à jour de l'UI.
      this.#ngZone.runOutsideAngular(() => {
        this.#ws = io(`${this.apiUrl}`, {
          autoConnect: false,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
        });

        this.setupEventListeners();
      });
      this.socketInitialized = true;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de socket.io:', error);
    }
  }

  async emitUserEvent(type: 'visit' | 'session', action: 'increment' | 'decrement'): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }
    await this.initializeSocket();
    if (!this.#ws) {
      return;
    }
    if (this.connected) {
      this.#ws.emit('user-event', { type: type, action: action });
    } else {
      // La connexion est asynchrone : on émet dès qu'elle est établie
      // au lieu de jeter l'événement silencieusement
      this.#ws.once('connect', () => {
        this.#ws?.emit('user-event', { type: type, action: action });
      });
    }
  }

  private setupEventListeners(): void {
    if (!this.#ws) {
      return;
    }

    // Connexion
    this.#ws.on('connect', () => {
      this.connected = true;
    });

    // Déconnexion
    this.#ws.on('disconnect', (reason) => {
      this.connected = false;
    });

    // Nouvelle notification — le handler touche l'UI, on rentre dans la zone
    this.#ws.on('notification', (data: INotification) => {
      this.#ngZone.run(() => this.#notificationsService.addNotification({
        ...data,
        createdAt: new Date(data.createdAt)
      }));
    });

    // Nouvelle réservation
    this.#ws.on('new-reservation', (data: any) => {
      // Force update des réservations
      this.#ngZone.run(() => this.#reservationService.forceUpdate());
    });

    // Réservation mise à jour
    this.#ws.on('reservation-updated', (data: any) => {
      this.#ngZone.run(() => this.#reservationService.forceUpdate());
    });

    // Réservation annulée
    this.#ws.on('reservation-cancelled', (data: any) => {
      this.#ngZone.run(() => this.#reservationService.forceUpdate());
    });

    // Erreurs
    this.#ws.on('error', (error) => {
      console.error('Erreur WebSocket:', error);
    });
  }

  async on(event: string, callback: (data: any) => void): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }
    await this.initializeSocket();
    if (this.#ws) {
      this.#ws.on(event, callback);
    }
  }

  async off(event: string, callback?: (data: any) => void): Promise<void> {
    if (!isPlatformBrowser(this.#platformId) || !this.#ws) {
      return;
    }
    this.#ws.off(event, callback);
  }

  async connect(): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }
    await this.initializeSocket();
    if (!this.connected && this.#ws) {
      this.#ws.connect();
    }
  }

  disconnect(): void {
    if (!isPlatformBrowser(this.#platformId) || !this.#ws) {
      return;
    }
    if (this.connected) {
      this.#ws.disconnect();
      // Ne pas attendre l'événement 'disconnect' : un connect() immédiat
      // (ex: logout) doit voir l'état à jour
      this.connected = false;
    }
  }

  async joinRoom(roomId: string): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }
    await this.initializeSocket();
    if (this.#ws && this.connected) {
      this.#ws.emit('join-room', roomId);
    }
  }

  async leaveRoom(roomId: string): Promise<void> {
    if (!isPlatformBrowser(this.#platformId) || !this.#ws) {
      return;
    }
    if (this.connected) {
      this.#ws.emit('leave-room', roomId);
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

}
